import React, { useState } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import { yellow } from '@mui/material/colors';

import uniq from 'lodash/uniq';

import UMDLogo from './umd-logo.svg';
import questLogo from './quest_logo.png';

import './App.css';

import data from './alumni-data.json';

const theme = createTheme({
  palette: {
    primary: {
        main: '#b71c1c',
    },
    secondary: yellow,
  },
});

type ConditionalRenderProps = {
  label: string,
  value: string | null,
};
const ConditionalRenderer = ({value, label}: ConditionalRenderProps) => {
  if (!value) {
    return null;
  }
  return (                  
    <>
      <h4>{label}</h4>
      <div className='u-margin-left'>{value}</div>
    </>
  );
};

const getListFromSemicolonOrCommaValue = (value: string) => {
  let list = [];
  if (value.indexOf(';') === -1 && value.indexOf(',') >= 0) {
    list = value.split(',');
  } else {
    list = value.split(';');
  }
  return list.map(l => l.trim());
}

type SemicolonListProps = {
  value: string | null,
}
const SemicolonList = ({value}: SemicolonListProps) => {
  if (!value) {
    return null;
  }
  const list = getListFromSemicolonOrCommaValue(value);
  return (                  
    <ul>
      {list.map(item => <li key={item}>{item}</li>)}
    </ul>
  ); 
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const alphabeticalSort = (a: string, b: string) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());
const colleges = uniq(data.map(d => d.college)).sort(alphabeticalSort);
const cohorts = uniq(data.map(d => d.cohort).sort((a, b) => a-b));
const expertise = uniq([].concat.apply([], data.map(d => getListFromSemicolonOrCommaValue(d.expertise) as any)).sort(alphabeticalSort));

export const App = () => {
  const [alumniIndexToShow, setAlumniIndexToShow] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [selectedCollege, setSelectedCollege] = useState<string | null>(null);
  const [selectedCohort, setSelectedCohort] = useState<number | null>(null);
  const [selectedExpertise, setSelectedExpertise] = useState<string[]>([]);

  const handleAlumniClick = (index: number) => () => setAlumniIndexToShow(index);
  const handleClose = () => setAlumniIndexToShow(null);
  const stopPropagation = (event: any) => event.stopPropagation();

  const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLocaleLowerCase());
  };
  const handleSelectedCollege = (_event: any, newValue: string | null) => {
    setSelectedCollege(newValue);
  };
  const handleSelectedCohort = (_event: any, newValue: number | null) => {
    setSelectedCohort(newValue);
  };
  const handleExpertiseChange = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;
    setSelectedExpertise(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };
  const handleClearFilter = () => {
    setSelectedCollege(null);
    setSelectedCohort(null);
    setSelectedExpertise([]);
  }

  const filteredData = data.filter(d => {
    let shouldBeIncluded = true;
    if(selectedCollege) {
      shouldBeIncluded = d.college === selectedCollege;
    }
    if(shouldBeIncluded && selectedCohort) {
      shouldBeIncluded = d.cohort === selectedCohort;
    }
    if(shouldBeIncluded && searchTerm) {
      shouldBeIncluded = d.firstName.toLocaleLowerCase().includes(searchTerm) || d.lastName.toLocaleLowerCase().includes(searchTerm);
    }
    if(shouldBeIncluded && selectedExpertise.length) {
      shouldBeIncluded = selectedExpertise.some(val => d.expertise.includes(val)); 
    }
    return shouldBeIncluded;
  });

  const sortedData = filteredData.sort((a, b) => a.firstName.toLocaleLowerCase().localeCompare(b.firstName.toLocaleLowerCase()));
  const selectedAlumni = alumniIndexToShow !== null ? sortedData[alumniIndexToShow] : null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="relative">
        <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Grid item container direction="row" justifyContent="flex-start" alignItems="center" style={{ width: 'auto' }}>
            <Grid item>
              <img alt="the quest logo" src={questLogo} width="90px" style={{ margin: '5px', marginLeft: '10px'}}></img>
            </Grid>
          </Grid>
          <Grid item>
            <img src={UMDLogo} alt="The University Of Maryland Logo" width="300px" style={{ paddingRight: '20px' }} />
          </Grid>
        </Grid>
      </AppBar>
      <main>
        <Container sx={{ py: 8 }} maxWidth="lg" style={{ paddingTop: '2px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <h1>QUEST Alumni: Celebrating 30 Years</h1>
              In celebration of QUEST's 30th anniversary, we are excited to highlight the accomplishments of many of our incredible alumni. 
              Thanks to those who have volunteered to be featured! 
              We hope that this website will facilitate networking amongst the community. 
              Please use LinkedIn to get in touch with each other or reach out to Jessica Roffe (<a href="mailto:jroffe@umd.edu">jroffe@umd.edu</a>) for emails. 
              Happy networking, and we look forward to seeing everyone in person at our 30th Anniversary Celebration on November 12, 2022! 
              More information can be found at <a target="_blank" rel="noreferrer" href="https://go.umd.edu/quest30th">go.umd.edu/quest30th</a>.
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Search Name..." 
                variant="outlined" 
                value={searchTerm}
                onChange={handleSearchTermChange}
                style={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={3}>
              <FormControl style={{width : '100%'}}>
                <InputLabel>Expertise</InputLabel>
                <Select
                  labelId="demo-multiple-name-label"
                  id="demo-multiple-name"
                  multiple
                  value={selectedExpertise}
                  onChange={handleExpertiseChange}
                  input={<OutlinedInput label="Name" />}
                  MenuProps={MenuProps}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {expertise.map((exp) => (
                    <MenuItem key={exp} value={exp}>
                      <Checkbox checked={selectedExpertise.indexOf(exp) > -1} />
                      <ListItemText primary={exp} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <Autocomplete
                disablePortal
                options={colleges}
                renderInput={(params) => <TextField {...params} label="College" />}

                value={selectedCollege}
                onChange={handleSelectedCollege}
              />
            </Grid>
            <Grid item xs={3}>
              <Autocomplete
                disablePortal
                options={cohorts}
                renderInput={(params) => <TextField {...params} label="Cohort" />}

                value={selectedCohort}
                onChange={handleSelectedCohort}
              />
            </Grid>
            <Grid item xs={3}>
              <Button size="small" onClick={handleClearFilter} variant="outlined" style={{ width: '100%', height: '100%'}}>Clear Filters</Button>
            </Grid>
            {sortedData.map((questAlumni, index) => (
              <Grid item key={questAlumni.timestamp} xs={12} sm={6} md={3}>
                <Card sx={{ maxWidth: 345, cursor: 'pointer' }} onClick={handleAlumniClick(index)}>
                  {questAlumni.newPhoto ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={process.env.PUBLIC_URL + `/newPhotos/${questAlumni.newPhoto}`}
                      alt={`Picture of ${questAlumni.firstName} ${questAlumni.lastName}`}
                      classes={{
                        img: "u-objectFit-contain"
                      }}
                    />
                  ) : null}
                  <CardContent>
                    <h4 className="u-margin-sm">{questAlumni.firstName} {questAlumni.lastName}</h4>
                    <div>Cohort: {questAlumni.cohort}</div>
                    <div>College: {questAlumni.college}</div>                  
                    <div>Expertise:</div>
                    <SemicolonList value={questAlumni.expertise} />
                  </CardContent>
                  <CardActions className="u-spread">
                    {questAlumni.linkedIn ? <a target="_blank" rel="noreferrer" href={questAlumni.linkedIn} style={{ textDecoration: 'none' }}><Button size="small" onClick={stopPropagation}>Reach Out</Button></a> : null}
                    <Button size="small" onClick={handleAlumniClick(index)}>Read More</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
        {selectedAlumni ? (
          <Dialog onClose={handleClose} open scroll="paper">
            <DialogTitle>{selectedAlumni.firstName} {selectedAlumni.lastName}</DialogTitle>
            <DialogContent dividers>
              <DialogContentText>
                {selectedAlumni.questPhoto ? (
                  <CardMedia
                    component="img"
                    height="400"
                    image={process.env.PUBLIC_URL + `/questPhotos/${selectedAlumni.questPhoto}`}
                    alt={`Picture of ${selectedAlumni.firstName} ${selectedAlumni.lastName}`}
                    classes={{
                      img: "u-objectFit-contain"
                    }}
                  />
                ) : null}
                <h4>Basic Information</h4>
                <div className='u-margin-left'>
                  <div><b>Cohort:</b> {selectedAlumni.cohort}</div>
                  <div><b>College:</b> {selectedAlumni.college}</div>
                  <div><b>Majors:</b> {selectedAlumni.majors}</div>
                  <div><b>Location:</b> {selectedAlumni.location}</div>
                  <div><b>Work:</b> {selectedAlumni.work}</div>
                  <div><b>Graduate School:</b> {selectedAlumni.graduateSchool}</div>
                  <div><b>Expertise:</b></div>
                  <SemicolonList value={selectedAlumni.expertise} />
                  <div><b>LinkedIn:</b> <a target="_blank" rel="noreferrer" href={selectedAlumni.linkedIn}>{selectedAlumni.linkedIn}</a></div>
                </div>
                <ConditionalRenderer label="Professional Highlights" value={selectedAlumni.professionalHighlights} />
                <ConditionalRenderer label="Personal Highlights" value={selectedAlumni.personalHighlights} />
                <ConditionalRenderer label="What did you most appreciate about QUEST when you were a student?" value={selectedAlumni.questLikes} />
                <ConditionalRenderer label="What are 1-2 ways your experience with QUEST has helped you since graduating?" value={selectedAlumni.questHelps} />
                <ConditionalRenderer label="How would you say your plans changed since college? What did you think you'd be doing vs. what are you actually doing?" value={selectedAlumni.plansChanged} />
                <ConditionalRenderer label="Anything else to share?" value={selectedAlumni.anythingShare} />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
          </Dialog>
        ) : null}
      </main>
    </ThemeProvider>
  );
}

export default App;
